import { HttpClient } from "@angular/common/http";
import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PopupCallbackCode } from "@kno2/shared/popup-callback";
import { noop } from "@kno2/utils";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "kno2-authorization-code-callback",
    imports: [],
    template: ``
})
export class AuthorizationCodeCallbackComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly httpClient = inject(HttpClient);

    public async ngOnInit(): Promise<void> {
        try {
            await this.processAuthorizationCallback();
        } catch {
            noop();
        } finally {
            window.close();
        }
    }

    private async processAuthorizationCallback(): Promise<void> {
        const { type, code, state, error } = this.getRouteParams();

        if (!type) {
            return;
        }

        if (Object.values(PopupCallbackCode).includes(type as PopupCallbackCode)) {
            this.notifyParentWindow(type as PopupCallbackCode, state, code, error);
        } else {
            if (!code) {
                return;
            }

            await this.handleOAuthCallback(type, code);
        }
    }

    private getRouteParams() {
        const params = this.route.snapshot.paramMap;
        const queryParams = this.route.snapshot.queryParamMap;

        return {
            type: params.get("type"),
            code: queryParams.get("code"),
            state: queryParams.get("state"),
            error: queryParams.get("error")
        };
    }

    private notifyParentWindow(type: PopupCallbackCode, state: string, code?: string, error?: string): void {
        if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
                {
                    type,
                    state,
                    code,
                    error
                },
                window.location.origin
            );
        }
    }

    private async handleOAuthCallback(type: string, code: string): Promise<void> {
        const typeKey = type.toLowerCase();

        if (typeKey === "kno2docusign") {
            await this.callDocusignApi(code);
        } else {
            await this.callIntegrationsApi(typeKey, code);
        }
    }

    private async callDocusignApi(authorizationCode: string): Promise<void> {
        await firstValueFrom(
            this.httpClient.post<void>("/api/connectors/ds/authcode", {
                authorizationCode
            })
        );
    }

    private async callIntegrationsApi(type: string, authorizationCode: string): Promise<void> {
        const integrationMap: Record<string, string> = {
            docusign: "DocusignApi.DocusignApiIntegration"
        };

        await firstValueFrom(
            this.httpClient.post<void>("/api/integrations/authcode", {
                authorizationCode,
                integrationType: integrationMap[type]
            })
        );
    }
}
